/**
 * Calendar-style lane assignment for blocks sharing one schedule row.
 *
 * Every block that renders in a stage row — artist-bound crew assignments AND
 * stage-bound custom assignments — must split the row's vertical space with
 * every other block it overlaps in time, regardless of type. Historically each
 * type only checked overlap against its own kind, so a stage-bound custom
 * painted at full height straight over an artist-bound block, hiding it.
 *
 * The algorithm is the standard calendar one: sweep blocks in start order,
 * group transitively-overlapping blocks into clusters, and greedily give each
 * block the lowest lane that is free at its start. Every block in a cluster
 * shares the cluster's lane count, so heights are consistent — unlike the old
 * per-block pairwise counting, where neighbours could disagree on how many
 * slices the row had.
 */

export interface RowLaneSlot {
  /** Zero-based vertical slot within the row. */
  laneIndex: number;
  /** Total lanes in this block's overlap cluster (1 = full height). */
  laneCount: number;
}

export interface RowLaneRange {
  start: number;
  end: number;
}

/**
 * Highest laneCount in a lane map — how many parallel lanes the row (or
 * strip) needs to fit every overlap cluster.
 */
export function maxLaneCount(lanes: ReadonlyMap<unknown, RowLaneSlot>): number {
  let max = 0;
  for (const slot of lanes.values()) max = Math.max(max, slot.laneCount);
  return max;
}

/**
 * Assign lanes to `items`. Ranges are in any monotonic unit (minutes, pixels);
 * blocks that merely touch (end === next start) do NOT overlap. Ties on start
 * keep the input order, so callers control stacking priority (e.g. mirrors
 * last) by pre-sorting the list. Keyed by object identity, so duplicate rows
 * with identical times still get their own lane instead of hiding one another.
 */
export function assignRowLanes<T>(
  items: readonly T[],
  getRange: (item: T) => RowLaneRange,
): Map<T, RowLaneSlot> {
  const slots = new Map<T, RowLaneSlot>();

  const sorted = items
    .map((item, index) => {
      const { start, end } = getRange(item);
      return { item, index, start, end: Math.max(end, start) };
    })
    .sort((a, b) => a.start - b.start || a.index - b.index);

  let clusterMembers: { item: T; laneIndex: number }[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = () => {
    for (const member of clusterMembers) {
      slots.set(member.item, {
        laneIndex: member.laneIndex,
        laneCount: laneEnds.length,
      });
    }
    clusterMembers = [];
    laneEnds = [];
  };

  for (const entry of sorted) {
    if (entry.start >= clusterEnd) closeCluster();
    let lane = laneEnds.findIndex((end) => end <= entry.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(entry.end);
    } else {
      laneEnds[lane] = entry.end;
    }
    clusterMembers.push({ item: entry.item, laneIndex: lane });
    clusterEnd = Math.max(clusterEnd, entry.end);
  }
  closeCluster();

  return slots;
}
